import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import RichContent from '../components/RichContent';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getProducts, getLocations, createSale, searchCustomers } from '../services/api';
import { listPaymentTerminals } from '../services/paymentTerminalsApi';
import useGeolocation from '../hooks/useGeolocation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import EmailWithDomainPicker from '../components/EmailWithDomainPicker';
import PhoneWithCountryPicker from '../components/PhoneWithCountryPicker';
import {
    ShoppingCart,
    Plus,
    Minus,
    Trash,
    User,
    Envelope,
    Phone,
    WhatsappLogo,
    ChatCircleText,
    Check,
    X,
    Tag,
    MapPin,
    MagnifyingGlass,
    Receipt,
    Certificate,
    Bed,
    Calendar,
    Printer,
    CreditCard,
    Bank,
    Money,
    Gift,
    Warning
} from '@phosphor-icons/react';

const POSPage = () => {
    const { user, fetchUser } = useAuth();
    const navigate = useNavigate();
    const geo = useGeolocation();
    const gpsCheckedRef = useRef(false);
    const [products, setProducts] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Cart state
    const [cart, setCart] = useState([]);
    
    // Customer state
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerResults, setCustomerResults] = useState([]);
    const [customerData, setCustomerData] = useState({
        name: '',
        email: '',
        phone: ''
    });
    
    // Delivery & location
    const [selectedLocation, setSelectedLocation] = useState('');
    const [notes, setNotes] = useState('');
    const [internalNotes, setInternalNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash'); // cash | credit_card | debit_card
    const [cardLast4, setCardLast4] = useState('');
    const [tpvId, setTpvId] = useState('');
    const [operationNumber, setOperationNumber] = useState('');
    const [isCourtesy, setIsCourtesy] = useState(false);
    const [tpvList, setTpvList] = useState([]);
    // Mixed payment (split between 2 methods). When enabled, paymentMethod is ignored
    // and payments[0] inherits the current single-payment fields.
    const [mixedPayment, setMixedPayment] = useState(false);
    const [splitPayments, setSplitPayments] = useState([
        { method: 'cash', amount: '', card_last4: '', tpv_id: '', operation_number: '' },
        { method: 'cash', amount: '', card_last4: '', tpv_id: '', operation_number: '' },
    ]);
    
    // Modal states
    const [showCheckout, setShowCheckout] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [lastSale, setLastSale] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    const searchTimeout = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    // React to mid-shift location changes via the Sidebar LocationSelectorModal.
    // user.current_location_id changes → immediately reflect in the POS cart's
    // selectedLocation so the next sale uses the freshly chosen kiosk.
    useEffect(() => {
        if (user?.current_location_id) {
            setSelectedLocation(user.current_location_id);
        }
    }, [user?.current_location_id]);

    // GPS re-verification when a seller opens POS. Runs once per mount.
    // If GPS resolves to a DIFFERENT active location than the one currently set,
    // prompt the seller to switch (non-blocking toast). Silent no-op on failure
    // — the existing manual location is preserved.
    const reVerifyGpsLocation = useCallback(async () => {
        if (gpsCheckedRef.current) return;
        if (user?.role !== 'seller') return;
        // Need at least one GPS-configured location to bother
        const anyGeoLoc = locations.some(l => l.latitude != null && l.longitude != null);
        if (!anyGeoLoc) return;
        gpsCheckedRef.current = true;
        try {
            const coords = await geo.request({ highAccuracy: true, timeout: 8000, maximumAge: 60000 });
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/locations/resolve-gps`,
                { latitude: coords.latitude, longitude: coords.longitude }
            );
            if (data.matched && data.location && data.location.id !== user?.current_location_id) {
                toast.info(
                    `📍 Estás cerca de ${data.location.name}`,
                    {
                        description: `A ${Math.round(data.distance_m)} m de distancia. ¿Cambiar tu ubicación de trabajo?`,
                        duration: 15000,
                        action: {
                            label: 'Cambiar',
                            onClick: async () => {
                                try {
                                    await axios.patch(
                                        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/current-location`,
                                        { location_id: data.location.id }
                                    );
                                    await fetchUser();
                                    sessionStorage.setItem(`location_selected_${user?.id}`, data.location.id);
                                    setSelectedLocation(data.location.id);
                                    toast.success(`Trabajando desde ${data.location.name}`);
                                } catch {
                                    toast.error('No se pudo cambiar la ubicación');
                                }
                            },
                        },
                    }
                );
            }
        } catch {
            // Silent — user already has a location set. No nagging.
        }
    }, [user?.role, user?.current_location_id, user?.id, locations]);

    useEffect(() => {
        // Only fire once locations are loaded and user role is known
        if (!loading && locations.length > 0 && user?.role === 'seller') {
            reVerifyGpsLocation();
        }
    }, [loading, locations.length, user?.role, reVerifyGpsLocation]);

    const fetchData = async () => {
        try {
            const [productsRes, locationsRes] = await Promise.all([
                getProducts(true),
                getLocations()
            ]);
            setProducts(productsRes.data);
            setLocations(locationsRes.data);
            
            // Set default location: user's session choice (LocationSelectorModal)
            // takes priority over the fixed base assignment. Falls back to the
            // first available location if neither is set.
            if (user?.current_location_id) {
                setSelectedLocation(user.current_location_id);
            } else if (user?.location_id) {
                setSelectedLocation(user.location_id);
            } else if (locationsRes.data.length > 0) {
                setSelectedLocation(locationsRes.data[0].id);
            }

            // Load active TPVs (used when payment method is card)
            try {
                const tpvs = await listPaymentTerminals();
                setTpvList((tpvs || []).filter((t) => t.is_active));
            } catch { /* non-fatal */ }
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSearch = async (query) => {
        setCustomerSearch(query);
        
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        
        if (query.length < 2) {
            setCustomerResults([]);
            return;
        }
        
        searchTimeout.current = setTimeout(async () => {
            try {
                const response = await searchCustomers(query);
                setCustomerResults(response.data);
            } catch (error) {
                console.error('Search error:', error);
            }
        }, 300);
    };

    const selectCustomer = (customer) => {
        setCustomerData({
            name: customer.name,
            email: customer.email || '',
            phone: customer.phone || ''
        });
        setCustomerSearch('');
        setCustomerResults([]);
    };

    // Cart functions
    const addToCart = (product) => {
        const existingItem = cart.find(item => item.product.id === product.id);
        if (existingItem) {
            setCart(cart.map(item => 
                item.product.id === product.id 
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            ));
        } else {
            setCart([...cart, { product, quantity: 1 }]);
        }
        toast.success(`${product.name} agregado al carrito`);
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.product.id !== productId));
    };

    const updateQuantity = (productId, delta) => {
        setCart(cart.map(item => {
            if (item.product.id === productId) {
                const newQty = item.quantity + delta;
                return newQty > 0 ? { ...item, quantity: newQty } : item;
            }
            return item;
        }).filter(item => item.quantity > 0));
    };

    const getCartTotal = () => {
        return cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    };

    const getCartItemsCount = () => {
        return cart.reduce((sum, item) => sum + item.quantity, 0);
    };

    // Phone validation: needs exactly 10 digits (after stripping the dial code)
    const getDigitsOnly = (str) => (str || '').replace(/\D/g, '');
    const phoneHas10Digits = () => {
        const digits = getDigitsOnly(customerData.phone);
        // Phone is stored as "+52 5512345678". Strip the country dial digits.
        // Easier: just check that the LAST 10 chars are digits and the total has >= 10.
        return digits.length >= 10 && digits.slice(-10).length === 10;
    };

    const isCheckoutReady = () => {
        if (!customerData.name?.trim()) return false;
        if (cart.length === 0) return false;
        if (!phoneHas10Digits()) return false;
        const total = getCartTotal();
        if (total === 0) {
            // Total $0 → automáticamente cortesía. La UI ya no muestra checkbox.
            return true;
        }
        if (mixedPayment) {
            // Validate exactly 2 payments, both with amount > 0, sum = total
            let sum = 0;
            for (const p of splitPayments) {
                const amt = parseFloat(p.amount);
                if (!amt || amt <= 0) return false;
                if (p.method === 'credit_card' || p.method === 'debit_card') {
                    if (!/^\d{4}$/.test(p.card_last4 || '')) return false;
                    if (!p.tpv_id) return false;
                    if (!/^\d{4}$/.test(p.operation_number || '')) return false;
                }
                sum += amt;
            }
            if (Math.abs(sum - total) > 0.01) return false;
            return true;
        }
        if (paymentMethod === 'credit_card' || paymentMethod === 'debit_card') {
            if (!cardLast4 || !/^\d{4}$/.test(cardLast4)) return false;
            if (!tpvId) return false;
            if (!operationNumber || !/^\d{4}$/.test(operationNumber)) return false;
        }
        return true;
    };

    const clearCart = () => {
        setCart([]);
        setCustomerData({ name: '', email: '', phone: '' });
        setNotes('');
        setInternalNotes('');
        setCardLast4('');
        setTpvId('');
        setOperationNumber('');
        setIsCourtesy(false);
        setPaymentMethod('cash');
        setMixedPayment(false);
        setSplitPayments([
            { method: 'cash', amount: '', card_last4: '', tpv_id: '', operation_number: '' },
            { method: 'cash', amount: '', card_last4: '', tpv_id: '', operation_number: '' },
        ]);
    };

    const handleCheckout = async () => {
        if (!customerData.name) {
            toast.error('Ingresa el nombre del cliente');
            return;
        }
        
        if (cart.length === 0) {
            toast.error('El carrito está vacío');
            return;
        }

        if (!phoneHas10Digits()) {
            toast.error('Captura un teléfono de 10 dígitos');
            return;
        }

        const total = getCartTotal();
        // Total 0 → automáticamente cortesía (sin necesidad de checkbox)
        const effectiveIsCourtesy = total === 0 ? true : isCourtesy;
        // Mixed payment validation
        if (total > 0 && mixedPayment) {
            let sum = 0;
            for (let i = 0; i < splitPayments.length; i++) {
                const p = splitPayments[i];
                const amt = parseFloat(p.amount);
                if (!amt || amt <= 0) {
                    toast.error(`Captura el monto del pago ${i + 1}`);
                    return;
                }
                if (p.method === 'credit_card' || p.method === 'debit_card') {
                    if (!/^\d{4}$/.test(p.card_last4 || '')) {
                        toast.error(`Captura los últimos 4 dígitos del pago ${i + 1}`);
                        return;
                    }
                    if (!p.tpv_id) {
                        toast.error(`Selecciona el TPV del pago ${i + 1}`);
                        return;
                    }
                    if (!/^\d{4}$/.test(p.operation_number || '')) {
                        toast.error(`Captura el Nº de operación del pago ${i + 1}`);
                        return;
                    }
                }
                sum += amt;
            }
            if (Math.abs(sum - total) > 0.01) {
                toast.error(`La suma ($${sum.toFixed(2)}) debe ser igual al total ($${total.toFixed(2)})`);
                return;
            }
        }
        // Single card payment validation
        if (total > 0 && !mixedPayment && (paymentMethod === 'credit_card' || paymentMethod === 'debit_card')) {
            if (!cardLast4 || cardLast4.length !== 4 || !/^\d{4}$/.test(cardLast4)) {
                toast.error('Captura los últimos 4 dígitos de la tarjeta');
                return;
            }
            if (!tpvId) {
                toast.error('Selecciona el TPV utilizado');
                return;
            }
            if (!operationNumber || !/^\d{4}$/.test(operationNumber)) {
                toast.error('Captura el Nº de operación (4 dígitos)');
                return;
            }
        }
        
        setSubmitting(true);
        
        try {
            const saleData = {
                customer_name: customerData.name,
                customer_email: customerData.email || null,
                customer_phone: customerData.phone || null,
                items: cart.map(item => ({
                    product_id: item.product.id,
                    quantity: item.quantity
                })),
                location_id: selectedLocation,
                delivery_method: "all",
                payment_method: mixedPayment ? 'mixed' : paymentMethod,
                card_last4: !mixedPayment && (paymentMethod === 'credit_card' || paymentMethod === 'debit_card') ? cardLast4 : null,
                tpv_id: !mixedPayment && (paymentMethod === 'credit_card' || paymentMethod === 'debit_card') ? tpvId : null,
                operation_number: !mixedPayment && (paymentMethod === 'credit_card' || paymentMethod === 'debit_card') ? operationNumber : null,
                payments: mixedPayment ? splitPayments.map(p => ({
                    method: p.method,
                    amount: parseFloat(p.amount),
                    card_last4: (p.method === 'credit_card' || p.method === 'debit_card') ? p.card_last4 : null,
                    tpv_id: (p.method === 'credit_card' || p.method === 'debit_card') ? p.tpv_id : null,
                    operation_number: (p.method === 'credit_card' || p.method === 'debit_card') ? p.operation_number : null,
                })) : null,
                is_courtesy: getCartTotal() === 0 ? true : false,
                notes: notes || null,
                internal_notes: internalNotes || null
            };
            
            const response = await createSale(saleData);
            setLastSale(response.data);
            
            setShowCheckout(false);
            setShowSuccess(true);
            clearCart();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al procesar la venta');
        } finally {
            setSubmitting(false);
        }
    };

    const sendNotification = async (sale) => {
        // Multi-channel sending is now handled by the backend automatically
        // The create_sale endpoint sends to ALL available channels
        console.log('Notifications sent via backend multichannel', sale?.id);
    };

    const handlePrintReceipt = () => {
        if (!lastSale) return;
        const printWindow = window.open('', '_blank');
        const folios = lastSale.items?.map(i => i.folio).join(', ') || '';
        const items = lastSale.items?.map(i => 
            `<tr><td style="padding:8px;border-bottom:1px solid #eee">${i.product_name || 'Certificado'}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:right">$${i.price?.toLocaleString()}</td></tr>`
        ).join('') || '';
        
        printWindow.document.write(`
            <html><head><title>Recibo - ${folios}</title>
            <style>
                body { font-family: 'Courier New', monospace; max-width: 380px; margin: 0 auto; padding: 20px; color: #0F1B3D; }
                .header { text-align: center; border-bottom: 2px dashed #ccc; padding-bottom: 15px; margin-bottom: 15px; }
                .header h1 { font-size: 18px; margin: 0; }
                .header p { font-size: 11px; color: #666; margin: 4px 0; }
                table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                .total { font-size: 18px; font-weight: bold; text-align: right; border-top: 2px solid #0F1B3D; padding-top: 10px; }
                .footer { text-align: center; font-size: 10px; color: #888; border-top: 2px dashed #ccc; padding-top: 15px; margin-top: 15px; }
                .folio { background: #f5f2ea; padding: 8px; border-radius: 4px; text-align: center; font-weight: bold; margin: 10px 0; }
            </style></head>
            <body>
                <div class="header">
                    <h1>EXPLOREMOS</h1>
                    <p>Certificados de Viaje</p>
                    <p>${new Date(lastSale.created_at).toLocaleString('es-MX')}</p>
                </div>
                <p><strong>Cliente:</strong> ${lastSale.customer?.name || customerData.name}</p>
                <table>${items}</table>
                <div class="total">TOTAL: $${lastSale.total_amount?.toLocaleString()}</div>
                <div class="folio">Folio(s): ${folios}</div>
                <div class="footer">
                    <p>¡Gracias por tu compra!</p>
                    <p>Para reservar, contacta a reservaciones</p>
                    <p>WhatsApp: +52 13333202200</p>
                </div>
            </body></html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const calculateDiscount = (original, current) => {
        if (!original || original <= current) return null;
        return Math.round(((original - current) / original) * 100);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="pos-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        {user?.role === 'seller' ? 'Punto de Activación' : 'Punto de Venta'}
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        {user?.role === 'seller'
                            ? 'Selecciona las activaciones para agregar al carrito'
                            : 'Selecciona los certificados para agregar al carrito'}
                    </p>
                </div>
                
                {/* Cart Summary Button */}
                <Button
                    onClick={() => cart.length > 0 && setShowCheckout(true)}
                    disabled={cart.length === 0}
                    data-testid="open-cart-btn"
                    className="h-14 px-6 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-2xl font-medium transition-all duration-200 active:scale-[0.98] relative"
                >
                    <ShoppingCart size={24} weight="duotone" className="mr-3" />
                    <span className="text-lg">{formatCurrency(getCartTotal())}</span>
                    {cart.length > 0 && (
                        <span className="absolute -top-2 -right-2 w-7 h-7 bg-[#1F2D4B] rounded-full flex items-center justify-center text-sm font-bold">
                            {getCartItemsCount()}
                        </span>
                    )}
                </Button>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.length === 0 ? (
                    <Card className="col-span-full bg-white border-[#ECE3D2] rounded-2xl">
                        <CardContent className="py-12 text-center">
                            <Certificate size={48} weight="duotone" className="mx-auto mb-4 text-[#4B5878] opacity-50" />
                            <p className="text-[#4B5878]">No hay productos disponibles</p>
                        </CardContent>
                    </Card>
                ) : (
                    products.map((product) => {
                        const discount = calculateDiscount(product.original_price, product.price);
                        const inCart = cart.find(item => item.product.id === product.id);
                        
                        return (
                            <Card 
                                key={product.id}
                                className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden card-hover group"
                            >
                                {/* Product Image/Header */}
                                <div 
                                    className="h-40 relative overflow-hidden"
                                    style={{
                                        backgroundColor: product.design?.background_color || '#F9FAFB',
                                        backgroundImage: product.design?.background_image 
                                            ? `url(${product.design.background_image})`
                                            : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    {/* Discount Badge */}
                                    {discount && (
                                        <div className="absolute top-3 left-3">
                                            <Badge className="bg-[#FB7185] text-white border-0 px-3 py-1 text-sm font-bold shadow-md">
                                                -{discount}%
                                            </Badge>
                                        </div>
                                    )}
                                    
                                    {/* Code Badge */}
                                    <div className="absolute top-3 right-3">
                                        <Badge className="bg-white/90 text-[#1F2D4B] border-0 px-2 py-1 font-mono text-xs shadow-sm">
                                            {product.code_prefix}
                                        </Badge>
                                    </div>
                                </div>
                                
                                <CardContent className="p-4">
                                    {/* Product Name */}
                                    <h3 className="font-editorial font-semibold text-[#1F2D4B] text-lg leading-tight mb-2">
                                        {product.name}
                                    </h3>
                                    
                                    {/* Quick Info */}
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <Badge variant="outline" className="border-[#ECE3D2] text-[#4B5878] text-xs">
                                            <MapPin size={12} className="mr-1" />
                                            {product.destination}
                                        </Badge>
                                        <Badge variant="outline" className="border-[#ECE3D2] text-[#4B5878] text-xs">
                                            <Bed size={12} className="mr-1" />
                                            {product.nights} Noches
                                        </Badge>
                                    </div>
                                    
                                    {/* Description */}
                                    <p className="text-sm text-[#4B5878] line-clamp-2 mb-4">
                                        {product.brief_description}
                                    </p>
                                    
                                    {/* Price */}
                                    <div className="flex items-baseline gap-2 mb-4">
                                        <span className="font-editorial text-2xl font-bold text-[#1F2D4B]">
                                            {formatCurrency(product.price)}
                                        </span>
                                        {product.original_price && product.original_price > product.price && (
                                            <span className="text-sm text-[#4B5878] line-through">
                                                {formatCurrency(product.original_price)}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => setSelectedProduct(product)}
                                            data-testid={`view-product-${product.id}`}
                                            className="flex-1 h-11 rounded-xl border-[#ECE3D2] hover:bg-[#F8F4EC]"
                                        >
                                            Ver Detalles
                                        </Button>
                                        
                                        {inCart ? (
                                            <div className="flex items-center gap-1 bg-[#F8F4EC] rounded-xl px-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => updateQuantity(product.id, -1)}
                                                    className="h-9 w-9 p-0 rounded-lg"
                                                >
                                                    <Minus size={16} />
                                                </Button>
                                                <span className="w-8 text-center font-semibold text-[#1F2D4B]">
                                                    {inCart.quantity}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => updateQuantity(product.id, 1)}
                                                    className="h-9 w-9 p-0 rounded-lg"
                                                >
                                                    <Plus size={16} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button
                                                onClick={() => addToCart(product)}
                                                data-testid={`add-to-cart-${product.id}`}
                                                className="h-11 px-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                                            >
                                                <Plus size={20} weight="bold" />
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </div>

            {/* Product Detail Modal */}
            <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden p-0">
                    {selectedProduct && (
                        <>
                            {/* Header Image */}
                            <div 
                                className="h-48 relative"
                                style={{
                                    backgroundColor: selectedProduct.design?.background_color || '#F9FAFB',
                                    backgroundImage: selectedProduct.design?.background_image 
                                        ? `url(${selectedProduct.design.background_image})`
                                        : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                <div className="absolute top-3 left-3">
                                    <Badge className="bg-white/90 text-[#1F2D4B] border-0 font-mono shadow-sm">
                                        {selectedProduct.code_prefix}
                                    </Badge>
                                </div>
                            </div>
                            
                            <div className="p-6 space-y-6">
                                <h2 className="font-editorial text-2xl font-bold text-[#1F2D4B]">
                                    {selectedProduct.name}
                                </h2>
                                {/* Price & Info */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-editorial text-3xl font-bold text-[#1F2D4B]">
                                                {formatCurrency(selectedProduct.price)}
                                            </span>
                                            {selectedProduct.original_price > selectedProduct.price && (
                                                <span className="text-lg text-[#4B5878] line-through">
                                                    {formatCurrency(selectedProduct.original_price)}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-[#4B5878]">Precio no modificable</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-[#1F2D4B]">{selectedProduct.destination}</p>
                                        <p className="text-sm text-[#4B5878]">{selectedProduct.hotel_category}</p>
                                    </div>
                                </div>
                                
                                {/* Description */}
                                <div>
                                    <h4 className="font-medium text-[#1F2D4B] mb-2">Descripción</h4>
                                    <RichContent value={selectedProduct.full_description || selectedProduct.brief_description} className="text-[#4B5878] text-sm leading-relaxed" />
                                </div>
                                
                                {/* Includes */}
                                {selectedProduct.includes?.length > 0 && (
                                    <div>
                                        <h4 className="font-medium text-[#1F2D4B] mb-2">Incluye</h4>
                                        <ul className="space-y-1">
                                            {selectedProduct.includes.map((item, i) => (
                                                <li key={i} className="flex items-center gap-2 text-sm text-[#4B5878]">
                                                    <Check size={16} className="text-[#059669]" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* Excludes */}
                                {selectedProduct.excludes?.length > 0 && (
                                    <div>
                                        <h4 className="font-medium text-[#1F2D4B] mb-2">No Incluye</h4>
                                        <ul className="space-y-1">
                                            {selectedProduct.excludes.map((item, i) => (
                                                <li key={i} className="flex items-center gap-2 text-sm text-[#4B5878]">
                                                    <X size={16} className="text-[#FB7185]" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* How to Book */}
                                <div className="bg-[#F8F4EC] rounded-xl p-4">
                                    <h4 className="font-medium text-[#1F2D4B] mb-2">Cómo Reservar</h4>
                                    <RichContent value={selectedProduct.how_to_book} className="text-sm text-[#4B5878] leading-relaxed" />
                                </div>
                                
                                {/* Terms */}
                                <div>
                                    <h4 className="font-medium text-[#1F2D4B] mb-2">Términos y Condiciones</h4>
                                    <RichContent value={selectedProduct.terms_and_conditions} className="text-xs text-[#4B5878] leading-relaxed" />
                                </div>
                                
                                {/* Validity */}
                                <div className="flex items-center gap-2 text-sm text-[#4B5878]">
                                    <Calendar size={16} />
                                    <span>Válido por {selectedProduct.validity_days} días desde la compra</span>
                                </div>
                                
                                {/* Add to Cart */}
                                <Button
                                    onClick={() => {
                                        addToCart(selectedProduct);
                                        setSelectedProduct(null);
                                    }}
                                    data-testid="add-to-cart-detail-btn"
                                    className="w-full h-14 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl text-lg font-medium"
                                >
                                    <ShoppingCart size={24} className="mr-2" />
                                    Agregar al Carrito
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Checkout Modal */}
            <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden p-5 sm:p-6 bg-[#F9F7F2]">
                    <DialogHeader className="pr-6 border-b border-[#ECE3D2] pb-4">
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B] flex items-center gap-2">
                            <ShoppingCart size={22} weight="duotone" className="text-[#FB7185]" />
                            Finalizar Compra
                        </DialogTitle>
                        <DialogDescription className="text-[#8B8474]">
                            {getCartItemsCount()} certificado(s) en el carrito
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5">
                        {/* Cart Items */}
                        <div className="space-y-2">
                            {cart.map((item) => (
                                <div
                                    key={item.product.id}
                                    className="flex items-center gap-3 p-2.5 bg-white border border-[#ECE3D2] rounded-lg"
                                >
                                    <div
                                        className="w-12 h-12 rounded-md bg-cover bg-center flex-shrink-0"
                                        style={{
                                            backgroundColor: item.product.design?.background_color || '#E5E7EB',
                                            backgroundImage: item.product.design?.background_image
                                                ? `url(${item.product.design.background_image})`
                                                : 'none'
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm text-[#1F2D4B] truncate">{item.product.name}</p>
                                        <p className="text-xs text-[#8B8474]">{formatCurrency(item.product.price)} c/u</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateQuantity(item.product.id, -1)}
                                            className="h-7 w-7 p-0 rounded-md hover:bg-[#F4ECE0]"
                                        >
                                            <Minus size={12} />
                                        </Button>
                                        <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateQuantity(item.product.id, 1)}
                                            className="h-7 w-7 p-0 rounded-md hover:bg-[#F4ECE0]"
                                        >
                                            <Plus size={12} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeFromCart(item.product.id)}
                                            className="h-7 w-7 p-0 rounded-md text-[#FB7185] hover:bg-red-50"
                                        >
                                            <Trash size={12} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Total */}
                        <div className="flex justify-between items-center py-3 border-t border-b border-[#ECE3D2]">
                            <span className="text-sm font-semibold text-[#1F2D4B] uppercase tracking-wider">Total</span>
                            <span className="font-editorial text-2xl font-bold text-[#1F2D4B]">{formatCurrency(getCartTotal())}</span>
                        </div>

                        {/* Customer Info */}
                        <div className="space-y-3">
                            <h4 className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold flex items-center gap-1.5">
                                <User size={13} weight="bold" />
                                Cliente
                            </h4>

                            {/* Search existing */}
                            <div className="relative">
                                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B8474]" />
                                <Input
                                    placeholder="Buscar cliente existente…"
                                    value={customerSearch}
                                    onChange={(e) => handleCustomerSearch(e.target.value)}
                                    style={{ backgroundColor: '#FFFFFF' }}
                                    className="h-10 pl-9 rounded-md border-[#ECE3D2] text-sm shadow-none"
                                />
                                {customerResults.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#ECE3D2] rounded-md shadow-lg max-h-40 overflow-y-auto">
                                        {customerResults.map((customer) => (
                                            <button
                                                key={customer.id}
                                                type="button"
                                                onClick={() => selectCustomer(customer)}
                                                className="w-full p-2.5 text-left text-sm hover:bg-[#F4ECE0] transition-colors"
                                            >
                                                <p className="font-medium text-[#1F2D4B]">{customer.name}</p>
                                                <p className="text-xs text-[#8B8474]">{customer.email || customer.phone}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Input
                                placeholder="Nombre completo *"
                                value={customerData.name}
                                onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                                data-testid="checkout-customer-name"
                                style={{ backgroundColor: '#FFFFFF' }}
                                className="h-10 rounded-md border-[#ECE3D2] text-sm shadow-none"
                            />

                            <div className="grid grid-cols-1 gap-3">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">Email</Label>
                                    <EmailWithDomainPicker
                                        value={customerData.email}
                                        onChange={(v) => setCustomerData({ ...customerData, email: v })}
                                        compact
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">
                                        Teléfono <span className="text-[#B91C1C]">*</span>
                                    </Label>
                                    <PhoneWithCountryPicker
                                        value={customerData.phone}
                                        onChange={(v) => setCustomerData({ ...customerData, phone: v })}
                                        compact
                                    />
                                    {customerData.phone && !phoneHas10Digits() && (
                                        <p className="text-[10px] text-[#B91C1C] mt-1 flex items-center gap-1">
                                            <Warning size={10} weight="bold" />
                                            Debe tener 10 dígitos
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Payment method */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <h4 className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold">
                                    {getCartTotal() === 0 ? 'Autorización' : 'Forma de pago'}
                                </h4>
                                {getCartTotal() > 0 && (
                                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="mixed-payment-toggle">
                                        <input
                                            type="checkbox"
                                            checked={mixedPayment}
                                            onChange={(e) => {
                                                const enabled = e.target.checked;
                                                setMixedPayment(enabled);
                                                if (enabled) {
                                                    // Carry over the current single-payment fields as payment #1
                                                    setSplitPayments(prev => [
                                                        {
                                                            method: paymentMethod,
                                                            amount: prev[0].amount,
                                                            card_last4: cardLast4,
                                                            tpv_id: tpvId,
                                                            operation_number: operationNumber,
                                                        },
                                                        prev[1],
                                                    ]);
                                                }
                                            }}
                                            className="w-4 h-4 accent-[#1F2D4B]"
                                            data-testid="mixed-payment-checkbox"
                                        />
                                        <span className="text-xs font-semibold text-[#1F2D4B]">Pago mixto</span>
                                    </label>
                                )}
                            </div>

                            {getCartTotal() === 0 ? (
                                <div
                                    className="flex items-start gap-3 p-3 rounded-md border-2 border-[#FB7185]/40 bg-[#FDF5F5]"
                                    data-testid="courtesy-banner"
                                >
                                    <Gift size={18} weight="duotone" className="text-[#FB7185] mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-[#1F2D4B]">
                                            Certificado de cortesía
                                        </p>
                                        <p className="text-[11px] text-[#8B8474] mt-0.5">
                                            Total $0. Se registrará automáticamente como cortesía y quedará vinculado a tu usuario.
                                        </p>
                                    </div>
                                </div>
                            ) : mixedPayment ? (
                                <MixedPaymentEditor
                                    payments={splitPayments}
                                    onChange={setSplitPayments}
                                    total={getCartTotal()}
                                    tpvList={tpvList}
                                    formatCurrency={formatCurrency}
                                />
                            ) : (
                                <>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { value: 'cash', label: 'Efectivo', icon: Money, testid: 'payment-cash-btn' },
                                            { value: 'credit_card', label: 'Crédito', icon: CreditCard, testid: 'payment-credit-btn' },
                                            { value: 'debit_card', label: 'Débito', icon: Bank, testid: 'payment-debit-btn' },
                                        ].map(({ value, label, icon: Icon, testid }) => {
                                            const active = paymentMethod === value;
                                            return (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    onClick={() => {
                                                        setPaymentMethod(value);
                                                        if (value === 'cash') { setCardLast4(''); setTpvId(''); setOperationNumber(''); }
                                                    }}
                                                    data-testid={testid}
                                                    style={{ backgroundColor: active ? '#1F2D4B' : '#FFFFFF' }}
                                                    className={`flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-md border-2 text-sm font-medium transition-all ${
                                                        active
                                                            ? 'border-[#1F2D4B] text-white shadow-sm'
                                                            : 'border-[#ECE3D2] text-[#8B8474] hover:border-[#8B8474]'
                                                    }`}
                                                >
                                                    <Icon size={16} weight={active ? 'fill' : 'regular'} />
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {(paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                                        <div className="grid grid-cols-3 gap-2 pt-1" data-testid="card-payment-extra">
                                            <div>
                                                <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">Últ. 4 *</Label>
                                                <Input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="\d{4}"
                                                    maxLength={4}
                                                    value={cardLast4}
                                                    onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                                    placeholder="••••"
                                                    data-testid="card-last4-input"
                                                    style={{ backgroundColor: '#FFFFFF' }}
                                                    className="h-10 shadow-none border-[#ECE3D2] focus:border-[#1F2D4B] font-mono text-sm tracking-[0.2em] text-center"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">TPV *</Label>
                                                <select
                                                    value={tpvId}
                                                    onChange={(e) => setTpvId(e.target.value)}
                                                    data-testid="tpv-select"
                                                    style={{ backgroundColor: '#FFFFFF' }}
                                                    className="w-full h-10 px-2 rounded-md border border-[#ECE3D2] focus:outline-none focus:border-[#1F2D4B] text-sm"
                                                >
                                                    <option value="">—</option>
                                                    {tpvList.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">Nº Op. *</Label>
                                                <Input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="\d{4}"
                                                    maxLength={4}
                                                    value={operationNumber}
                                                    onChange={(e) => setOperationNumber(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                                    placeholder="••••"
                                                    data-testid="operation-number-input"
                                                    style={{ backgroundColor: '#FFFFFF' }}
                                                    className="h-10 shadow-none border-[#ECE3D2] focus:border-[#1F2D4B] font-mono text-sm tracking-[0.2em] text-center"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {(paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && tpvList.length === 0 && (
                                        <p className="text-[10px] text-[#B91C1C] flex items-center gap-1">
                                            <Warning size={10} weight="bold" />
                                            No hay TPVs registrados. Pide a tu administrador que los configure.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Delivery Channels — minimal */}
                        <div className="flex items-center gap-3 text-xs flex-wrap py-2">
                            <span className="text-[10px] uppercase tracking-wider text-[#1F2D4B] font-semibold">
                                Se enviará por
                            </span>
                            {customerData.phone && (
                                <>
                                    <span className="flex items-center gap-1 text-[#25D366]">
                                        <WhatsappLogo size={13} weight="fill" /> WhatsApp
                                    </span>
                                    <span className="flex items-center gap-1 text-[#1F2D4B]">
                                        <ChatCircleText size={13} weight="fill" /> SMS
                                    </span>
                                </>
                            )}
                            {customerData.email && (
                                <span className="flex items-center gap-1 text-[#FB7185]">
                                    <Envelope size={13} weight="fill" /> Email
                                </span>
                            )}
                            {!customerData.phone && !customerData.email && (
                                <span className="text-[#8B8474] italic">Agrega teléfono o email</span>
                            )}
                        </div>

                        {/* Internal Notes */}
                        <div className="space-y-1.5">
                            <div className="flex items-baseline justify-between">
                                <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold">
                                    Observaciones internas
                                </Label>
                                <span className="text-[9px] uppercase tracking-wider text-[#8B8474]">
                                    Sólo equipo
                                </span>
                            </div>
                            <textarea
                                value={internalNotes}
                                onChange={(e) => setInternalNotes(e.target.value)}
                                placeholder="Notas para el equipo (no se envían al cliente)."
                                data-testid="checkout-internal-notes"
                                style={{ backgroundColor: '#FFFFFF' }}
                                className="w-full min-h-[60px] p-2.5 rounded-md border border-[#ECE3D2] text-sm text-[#1F2D4B] focus:outline-none focus:border-[#1F2D4B] placeholder:text-[#8B8474]"
                            />
                        </div>

                        {/* Submit */}
                        <div className="flex gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowCheckout(false)}
                                className="flex-1 h-11 rounded-md border-[#ECE3D2] text-[#4B5878] hover:bg-[#F4ECE0] bg-white"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleCheckout}
                                disabled={submitting || !isCheckoutReady()}
                                data-testid="confirm-checkout-btn"
                                className="flex-[2] h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium disabled:bg-[#C8B996] disabled:text-white disabled:cursor-not-allowed disabled:hover:bg-[#C8B996]"
                            >
                                {submitting ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                    <>
                                        <Check size={16} className="mr-1.5" weight="bold" />
                                        Completar Venta · {formatCurrency(getCartTotal())}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Success Modal */}
            <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md rounded-2xl">
                    <div className="text-center py-4">
                        <div className="mx-auto w-20 h-20 bg-[#D1FAE5] rounded-full flex items-center justify-center mb-4">
                            <Check size={40} weight="bold" className="text-[#059669]" />
                        </div>
                        <h2 className="font-editorial text-2xl font-semibold text-[#1F2D4B] mb-2">
                            ¡Venta Completada!
                        </h2>
                        <p className="text-[#4B5878]">
                            Los certificados han sido generados y enviados al cliente
                        </p>
                    </div>
                    
                    {lastSale && (
                        <div className="space-y-3">
                            <div className="bg-[#F8F4EC] rounded-xl p-4 space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-[#4B5878]">Total</span>
                                    <span className="font-semibold text-[#1F2D4B]">{formatCurrency(lastSale.total_amount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-[#4B5878]">Certificados</span>
                                    <span className="font-semibold text-[#1F2D4B]">{lastSale.total_items}</span>
                                </div>
                                <div className="border-t border-[#ECE3D2] pt-3">
                                    <p className="text-xs text-[#4B5878] mb-2">Folios generados:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {lastSale.items?.map((item, i) => (
                                            <Badge key={i} variant="outline" className="font-mono text-xs border-[#1F2D4B] text-[#FB7185]">
                                                {item.folio}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Notification Status */}
                            {lastSale.notifications && (
                                <div className="p-3 rounded-xl border border-[#ECE3D2] bg-white">
                                    <p className="text-xs font-medium text-[#4B5878] mb-2">Notificaciones enviadas:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {lastSale.notifications.whatsapp === 'sent' && (
                                            <Badge className="bg-[#D1FAE5] text-[#059669] border-0 text-xs">
                                                <WhatsappLogo size={12} className="mr-1" /> WhatsApp
                                            </Badge>
                                        )}
                                        {lastSale.notifications.sms === 'sent' && (
                                            <Badge className="bg-[#D1FAE5] text-[#059669] border-0 text-xs">
                                                <ChatCircleText size={12} className="mr-1" /> SMS
                                            </Badge>
                                        )}
                                        {lastSale.notifications.email && (
                                            <Badge className="bg-amber-50 text-amber-700 border-0 text-xs">
                                                <Envelope size={12} className="mr-1" /> Email
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="flex gap-3">
                        <Button
                            onClick={() => lastSale?.id && navigate(`/tickets/${lastSale.id}`)}
                            data-testid="print-receipt-btn"
                            variant="outline"
                            className="flex-1 h-12 rounded-xl border-[#ECE3D2] text-[#1F2D4B]"
                        >
                            <Printer size={18} className="mr-2" />
                            Imprimir Ticket
                        </Button>
                        <Button
                            onClick={() => setShowSuccess(false)}
                            data-testid="new-sale-btn"
                            className="flex-1 h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl font-medium"
                        >
                            Nueva Venta
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// === Mixed Payment Editor ===
// Renders 2 stacked payment rows: each with method (cash/credit/debit), amount,
// and the bank fields (last4 + TPV + Op#) when method is a card.
const MixedPaymentEditor = ({ payments, onChange, total, tpvList, formatCurrency }) => {
    const update = (idx, patch) => {
        const next = payments.map((p, i) => (i === idx ? { ...p, ...patch } : p));
        onChange(next);
    };

    const paid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const remaining = total - paid;
    const diffOk = Math.abs(remaining) < 0.01;

    return (
        <div className="space-y-3">
            {payments.map((p, idx) => (
                <div
                    key={idx}
                    className="rounded-md border border-[#ECE3D2] p-3 space-y-2"
                    style={{ backgroundColor: '#FFFFFF' }}
                    data-testid={`split-payment-row-${idx}`}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold">
                            Pago {idx + 1}
                        </span>
                    </div>
                    <div className="grid grid-cols-[1.4fr_1fr] gap-2">
                        <select
                            value={p.method}
                            onChange={(e) => update(idx, {
                                method: e.target.value,
                                ...(e.target.value === 'cash'
                                    ? { card_last4: '', tpv_id: '', operation_number: '' }
                                    : {}),
                            })}
                            data-testid={`split-method-${idx}`}
                            style={{ backgroundColor: '#FFFFFF' }}
                            className="h-9 px-2 rounded-md border border-[#ECE3D2] focus:outline-none focus:border-[#1F2D4B] text-sm"
                        >
                            <option value="cash">Efectivo</option>
                            <option value="credit_card">Crédito</option>
                            <option value="debit_card">Débito</option>
                        </select>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={p.amount}
                            onChange={(e) => {
                                const v = e.target.value.replace(/[^\d.]/g, '');
                                update(idx, { amount: v });
                            }}
                            placeholder="$0.00"
                            data-testid={`split-amount-${idx}`}
                            style={{ backgroundColor: '#FFFFFF' }}
                            className="h-9 shadow-none border-[#ECE3D2] focus:border-[#1F2D4B] text-sm font-mono text-right"
                        />
                    </div>
                    {(p.method === 'credit_card' || p.method === 'debit_card') && (
                        <div className="grid grid-cols-3 gap-2">
                            <Input
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                value={p.card_last4}
                                onChange={(e) => update(idx, { card_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                placeholder="Últ. 4"
                                data-testid={`split-last4-${idx}`}
                                style={{ backgroundColor: '#FFFFFF' }}
                                className="h-9 shadow-none border-[#ECE3D2] focus:border-[#1F2D4B] text-sm font-mono text-center tracking-[0.15em]"
                            />
                            <select
                                value={p.tpv_id}
                                onChange={(e) => update(idx, { tpv_id: e.target.value })}
                                data-testid={`split-tpv-${idx}`}
                                style={{ backgroundColor: '#FFFFFF' }}
                                className="h-9 px-2 rounded-md border border-[#ECE3D2] focus:outline-none focus:border-[#1F2D4B] text-sm"
                            >
                                <option value="">TPV</option>
                                {tpvList.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                            <Input
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                value={p.operation_number}
                                onChange={(e) => update(idx, { operation_number: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                placeholder="Nº Op."
                                data-testid={`split-op-${idx}`}
                                style={{ backgroundColor: '#FFFFFF' }}
                                className="h-9 shadow-none border-[#ECE3D2] focus:border-[#1F2D4B] text-sm font-mono text-center tracking-[0.15em]"
                            />
                        </div>
                    )}
                </div>
            ))}
            <div
                className={`flex justify-between text-xs px-3 py-2 rounded-md ${
                    diffOk
                        ? 'bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]'
                        : 'bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]'
                }`}
                data-testid="mixed-payment-summary"
            >
                <span className="font-semibold">
                    {diffOk ? '✓ Suma exacta' : `Falta ${formatCurrency(Math.abs(remaining))}`}
                </span>
                <span className="font-mono">
                    {formatCurrency(paid)} / {formatCurrency(total)}
                </span>
            </div>
        </div>
    );
};

export default POSPage;
